import { DotLegalEmptyState, DotLegalPaper, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { Box, TableCell, TableRow } from "@mui/material";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { SharingRowTableModel, ThirdCountryOrigin } from "../ProcessingActivityOverview.types";
import useProcessingActivitySharingsTabHooks from "./ProcessingActivitySharingsTab.hooks";
import { StepTypeEnum } from "../../ProcessingActivity.types";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink";
import { useProcessingActivitySharingsTabStyles } from "./ProcessingActivitySharingsTab.styles";
import NoSharings from "./noSharings.svg?react";
import { informationIcon } from "../../../common/icons";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import MentionInputRender from "../../../common/components/mentionInput/MentionInputRender.tsx";

export interface IProcessingActivitySharingsTab {
    processingActivityId: string;
}

function ProcessingActivitySharingsTab(props: IProcessingActivitySharingsTab) {
    const { translateString } = useTranslation();
    const {
        rows,
        isLoading,
        hasSharings,
        isFilterBarLoading,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        getSharingLink,
        hasAccessToSharing,
    } = useProcessingActivitySharingsTabHooks(props);
    const styles = useProcessingActivitySharingsTabStyles();

    const getHeaders = () => {
        let headers = Array<ITableHeader<SharingRowTableModel>>();
        headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "senderName", text: translateString("Sender"), align: "left", showOnMobile: true });
        headers.push({
            property: "dataCategories",
            text: translateString("dataCategoryHeader"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({
            property: "recipientName",
            text: translateString("recipient"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({
            property: "legalBasisList",
            text: translateString("legalBasisHeader"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({
            property: "transferBasis",
            text: translateString("transferBasis"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({
            property: "deletionPeriods",
            text: translateString("deletionPeriods"),
            align: "left",
            showOnMobile: true,
        });

        headers.push({ property: "system", text: translateString("system"), align: "left", showOnMobile: true });

        return headers;
    };

    function getSharingLinkIfUserHasAccess(name: string, id: string, step: StepTypeEnum, subStep: StepTypeEnum | undefined, subId: string) {
        if (hasAccessToSharing(subStep)) {
            return (
                <DotLegalLink linkColor={"primary"} to={getSharingLink(id, step, subStep, subId)}>
                    {name}
                </DotLegalLink>
            );
        }

        return <div>{name}</div>;
    }

    return (
        <DotLegalPaper>
            {hasSharings ? (
                <DotLegalTableWithFilterBar
                    filterBar={{
                        isLoading: isFilterBarLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    headers={getHeaders()}
                    rowsPerPage={30}
                    clickableRows={false}
                    defaultOrderBy={"senderName"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    data={rows}
                    emptyText={translateString(hasActiveFilters ? "noSharingsMatchesQuery" : "noData")}
                    columnsExcludedFromFilter={["dataCategories"]}
                    renderRow={(row, i) => (
                        <TableRow key={i}>
                            <TableCell>
                                <>
                                    <DotLegalTooltip
                                        text={
                                            isNullOrWhitespace(row.description) ? undefined : <MentionInputRender value={row.description} linkColor={"white"} />
                                        }
                                    >
                                        <div>{row.name}</div>
                                    </DotLegalTooltip>
                                </>
                            </TableCell>
                            <TableCell>
                                <div>
                                    <Box sx={styles.legalEntity}>{row.senderName}</Box>
                                    {row.senderId !== null && (
                                        <Box sx={styles.entityRole}>
                                            {getSharingLinkIfUserHasAccess(
                                                row.senderRole,
                                                props.processingActivityId,
                                                StepTypeEnum.sharings,
                                                row!.subStep,
                                                row!.sharingId
                                            )}
                                        </Box>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div style={{ display: "flex", flexWrap: "wrap" }}>
                                    {row.dataCategories.map((x) => {
                                        return (
                                            <div
                                                key={x.id}
                                                style={{
                                                    marginRight: 8,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div style={{ display: "inline-block", marginRight: 3 }}>{x.name}</div>
                                                {!isNullOrWhitespace(x.description) && (
                                                    <DotLegalTooltip text={x.description}>
                                                        <Box sx={styles.tooltipIcon}>{informationIcon}</Box>
                                                    </DotLegalTooltip>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div>
                                    <Box
                                        sx={(theme) => ({
                                            color: theme.palette.primary.dark,
                                            display: "flex",
                                            alignItems: "center",
                                            "& svg": {
                                                fill:
                                                    row.thirdCountryOrigin === ThirdCountryOrigin.DataProcessor
                                                        ? theme.palette.error.light
                                                        : theme.palette.primary.dark,
                                                marginLeft: theme.spacing(0.5),
                                                height: 17,
                                                width: 17,
                                            },
                                        })}
                                    >
                                        {row.recipientName}
                                        {row.thirdCountryOrigin && (
                                            <DotLegalTooltip
                                                text={
                                                    row.thirdCountryOrigin === ThirdCountryOrigin.DataProcessor
                                                        ? translateString("recipientFromInsecureThirdCountry")
                                                        : translateString("dataStoredInThirdCountry")
                                                }
                                            >
                                                <ReportProblemOutlinedIcon fontSize={"small"} />
                                            </DotLegalTooltip>
                                        )}
                                    </Box>
                                    {row.recipientId !== null && (
                                        <Box sx={styles.entityRole}>
                                            {getSharingLinkIfUserHasAccess(
                                                row.recipientRole,
                                                props.processingActivityId,
                                                StepTypeEnum.sharings,
                                                row!.subStep,
                                                row!.sharingId
                                            )}
                                        </Box>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>
                                <Box sx={styles.entityRole}>
                                    {getSharingLinkIfUserHasAccess(
                                        row.legalBasisList.join(", "),
                                        props.processingActivityId,
                                        StepTypeEnum.sharings,
                                        StepTypeEnum.disclosure,
                                        row.sharingId
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>{row.transferBasis}</TableCell>
                            <TableCell>{row.deletionPeriods.join(", ")}</TableCell>

                            <TableCell>
                                {row.senderId != null ? (
                                    <Box sx={styles.entityRole}>
                                        {getSharingLinkIfUserHasAccess(row.system, props.processingActivityId, StepTypeEnum.systems, undefined, row.systemId)}
                                    </Box>
                                ) : (
                                    row.system
                                )}
                            </TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState icon={<NoSharings />} text={translateString("noSharingsOnProcessingActivity")} />
            )}
        </DotLegalPaper>
    );
}

export default ProcessingActivitySharingsTab;
